import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import API from "../../Utils/Interceptor/ApiMiddleware";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CustomSnackbar from "../Composables/CustomSnackBarMenu";

const CreateEmployer = ({
  open,
  onClose,
  fetchEmployerGridData,
  fetchEmployer,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone_cell: "",
    address: "",
    notes: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirm_password, setConfirm_Password] = useState("");
  const [statesData, setStatesData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [postalCodeData, setPostalCodeData] = useState([]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClose = () => {
    setFormData({
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      phone_cell: "",
      address: "",
      notes: "",
      city: "",
      state: "",
      zip_code: "",
    });
    onClose();
    setErrors({});
  };
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: name === "zip_code" ? String(value) : value,
      };
      if (name === "state") {
        updatedData.city = "";
        updatedData.zip_code = "";
        fetchCities(value); // Fetch cities based on the selected state
      } else if (name === "city") {
        updatedData.zip_code = ""; // Reset zip_code when city changes
        const payload = { city: value };
        fetchPostalCodes(payload); // Fetch new zip codes
      }

      return updatedData;
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value ? "" : prevErrors[name],
    }));
  };

  const fetchCities = async (stateName) => {
    try {
      const response = await API.post(
        process.env.REACT_APP_JOBEZEURL + "/state-city/list",
        { stateName: stateName }
      );
      setCitiesData(response?.data?.message || []);
    } catch (error) {
      console.error("Error fetching cities data", error);
    }
  };
  const fetchPostalCodes = async (payload) => {
    try {
      const response = await API.post(
        process.env.REACT_APP_JOBEZEURL + "/state-city/list",
        payload
      );
      setPostalCodeData(response?.data?.message || []);
    } catch (error) {
      console.error("Error fetching cities data", error);
    }
  };

  const handlePasswordChange = (e) => [setConfirm_Password(e.target.value)];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name) newErrors.first_name = "First Name is required";

    if (!formData.last_name) newErrors.last_name = "Last Name is required";

    if (!formData.email) newErrors.email = "Email is required";

    if (!formData.phone_cell) newErrors.phone_cell = "Phone Number is required";

    if (!/^\d{10}$/.test(formData.phone_cell.replace("+1 ", ""))) {
      newErrors.phone_cell = "Phone Number must be 10 digits";
    }

    if (!formData.notes) newErrors.notes = "Notes is required";

    if (formData.notes.split(" ").length > 500)
      newErrors.notes = "Charaters must be less than or equal to 500";

    if (!formData.address) newErrors.address = "Address is required";

    if (!formData.city) newErrors.city = "City is required";

    if (!formData.state) newErrors.state = "State is required";

    if (!formData.zip_code) newErrors.zip_code = "Zipcode is required";

    if (!formData.password) newErrors.password = "Password is required";
    else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        formData.password
      )
    ) {
      newErrors.password =
        "Password must be at least 8 characters long, contain uppercase and lowercase letters, a number, and a special character";
    }

    if (formData.password !== confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchStatesData = async () => {
    try {
      const response = await API.post(
        process.env.REACT_APP_JOBEZEURL + "/state-city/list",
        {}
      );
      setStatesData(response?.data?.message || []);
    } catch (error) {
      console.error("Error fetching state-city data", error);
    }
  };

  useEffect(() => {
    fetchStatesData();
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleAddEmployer = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      await API.post("/employer/create-employer", formData);
      setSnackbar({
        open: true,
        message: "Employer added successfully!",
        severity: "success",
      });
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Error adding employer:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add employer";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      }}
      maxWidth={false}
      fullWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "600px",
          height: "600px",
          borderRadius: "1rem",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px 24px",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: "22px" }}>
          Add Employer
        </Typography>
        <CloseIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ width: "50%" }}>
                <TextField
                  size="small"
                  margin="dense"
                  label="First Name"
                  fullWidth
                  required
                  variant="outlined"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
                  }}
                />
              </Box>

              {/* Last Name */}
              <Box sx={{ width: "50%" }}>
                <TextField
                  size="small"
                  margin="dense"
                  label="Last Name"
                  fullWidth
                  required
                  variant="outlined"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ width: "50%" }}>
                {/* <TextField
                  size="small"
                  margin="dense"
                  label="Phone"
                  fullWidth
                  required
                  variant="outlined"
                  name="phone_cell"
                  value={formData.phone_cell || "+1 "}
                  // onChange={(e) => {
                  //   if (e.target.value.length <= 10) {
                  //     handleInputChange(e);
                  //   }
                  // }}
                  onChange={(e) => {
                    let value = e.target.value;
  
                    if (!value.startsWith("+1 ")) {
                      value = "+1 ";
                    }
                    value = value.replace(/[^+\d\s]/g, "");
                    let numericPart = value.replace("+1 ", "").replace(/\s/g, "");
                    if (numericPart.length > 10) {
                      numericPart = numericPart.slice(0, 10);
                    }
                    handleInputChange({
                      target: { name: "phone", value: "+1 " + numericPart },
                    });
                  }}
                  error={!!errors.phone_cell}
                  helperText={errors.phone_cell}
                  inputProps={{ inputMode: "numeric" }}
                  onKeyDown={(e) => {
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete"
                    ) {
                      e.preventDefault();
                    }
                   if (
                    (formData.phone_cell || "+1 ") === "+1 " &&
                    e.key === "Backspace"
                  ) {
                    e.preventDefault();
                  }
                  let numericPart = formData.phone_cell
                    .replace("+1 ", "")
                    .replace(/\s/g, "");
                  if (numericPart.length >= 10 && /^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                  }}
                /> */}
                <TextField
                  size="small"
                  margin="dense"
                  label="Phone"
                  fullWidth
                  required
                  variant="outlined"
                  name="phone_cell"
                  value={formData.phone_cell || "+1 "}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Ensure the value always starts with "+1 "
                    if (!value.startsWith("+1 ")) {
                      value = "+1 ";
                    }

                    // Remove non-numeric characters except "+"
                    value = value.replace(/[^+\d\s]/g, "");

                    // Extract the numeric part (excluding "+1 ")
                    let numericPart = value
                      .replace("+1 ", "")
                      .replace(/\s/g, "");

                    // Limit to 10 digits
                    if (numericPart.length > 10) {
                      numericPart = numericPart.slice(0, 10);
                    }

                    // Update the state properly
                    handleInputChange({
                      target: {
                        name: "phone_cell",
                        value: `+1 ${numericPart}`,
                      },
                    });
                  }}
                  error={!!errors.phone_cell}
                  helperText={errors.phone_cell}
                  inputProps={{ inputMode: "numeric" }}
                  onKeyDown={(e) => {
                    // Prevent non-numeric characters except Backspace & Delete
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete"
                    ) {
                      e.preventDefault();
                    }

                    // Prevent backspace from deleting "+1 "
                    if (
                      (formData.phone_cell || "+1 ") === "+1 " &&
                      e.key === "Backspace"
                    ) {
                      e.preventDefault();
                    }

                    // Restrict input to 10 digits
                    let numericPart = (formData.phone_cell || "+1 ")
                      .replace("+1 ", "")
                      .replace(/\s/g, "");
                    if (numericPart.length >= 10 && /^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Box>
              {/* Email */}
              <Box sx={{ width: "50%" }}>
                <TextField
                  size="small"
                  margin="dense"
                  label="Email"
                  fullWidth
                  required
                  variant="outlined"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Select State */}
              <Box sx={{ width: "50%" }}>
                <TextField
                  size="small"
                  margin="dense"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  variant="outlined"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Confirm Password */}
              <Box sx={{ width: "50%" }}>
                <TextField
                  size="small"
                  margin="dense"
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  fullWidth
                  required
                  variant="outlined"
                  onChange={handlePasswordChange}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Notes */}
          <Box sx={{ width: "100%" }}>
            <TextField
              size="small"
              margin="dense"
              label="Write your notes here....."
              multiline
              required
              rows={3}
              fullWidth
              variant="outlined"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              error={!!errors.notes}
              helperText={errors.notes}
            />
          </Box>

          {/* Address */}
          <Box sx={{ width: "100%" }}>
            <TextField
              size="small"
              margin="dense"
              label="Write your address here....."
              fullWidth
              required
              variant="outlined"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Box>

          {/* State, City, and Postal Code */}
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Select State */}
              <Box sx={{ width: "33%" }}>
                <TextField
                  select
                  size="small"
                  margin="dense"
                  label="Select State"
                  fullWidth
                  required
                  variant="outlined"
                  defaultValue=""
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  error={!!errors.state}
                  helperText={errors.state}
                >
                  {statesData?.map((state) => (
                    <MenuItem key={state.name} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Select City */}
              <Box sx={{ width: "33%" }}>
                <TextField
                  select
                  size="small"
                  margin="dense"
                  label="Select City"
                  fullWidth
                  required
                  variant="outlined"
                  name="city"
                  value={formData.city}
                  disabled={!formData.state}
                  onChange={handleInputChange}
                  error={!!errors.city}
                  helperText={errors.city}
                >
                  {citiesData?.map((op) => (
                    <MenuItem key={op.city} value={op.city}>
                      {op.city}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Select Postal Code */}
              <Box sx={{ width: "33%" }}>
                <TextField
                  select
                  size="small"
                  margin="dense"
                  label="Select  Zipcode"
                  fullWidth
                  required
                  variant="outlined"
                  name="zip_code"
                  value={formData.zip_code}
                  disabled={!formData.city}
                  onChange={handleInputChange}
                  error={!!errors.zip_code}
                  helperText={errors.zip_code}
                >
                  {postalCodeData?.map((state) => (
                    <MenuItem key={state.zip} value={state.zip}>
                      {state.zip}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={{
          padding: "16px 24px",
          display: "flex",
          justifyContent: "left",
          gap: "20px",
        }}
      >
        <Button
          sx={{
            border: "1px solid #979797",
            color: "#00000099",
            padding: "4px 10px",
          }}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          sx={{
            backgroundColor: "#3F95EA",
            color: "white",
            padding: "4px 10px",
          }}
          onClick={handleAddEmployer}
        >
          Add Employer
        </Button>
      </DialogActions>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </Dialog>
  );
};

export default CreateEmployer;
